import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Button,
  Card,
  message,
  Typography,
  Space,
  PageHeader,
  Row,
  Col,
  Tag,
  Radio,
} from 'antd';
import {
  PrinterOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  rackRegistryGetByIdService,
  rackRegistryUpdateService,
  rackRegistryDiagramDataService,
} from 'services/rackregistry';
import { rackDeviceListService } from 'services/rackdevice';
import NetworkToolWrapper from '../../layouts/NetworkToolWrapper';
import { MyBreadcrumb, axiosBase } from 'utils';
import { useRackDiagramBreadcrumb } from '../../hooks/useRackDiagramBreadcrumb';
import RackDiagramGroup from '../../components/rack-diagram/RackDiagramGroup';
import PortLinkOverlay from '../../components/rack-diagram/PortLinkOverlay';
import PortDetailPanel from '../../components/rack-diagram/PortDetailPanel';
import EnhancedModeLegend from '../../components/rack-diagram/EnhancedModeLegend';
import { enhancedTheme } from '../../components/rack-diagram/utils';

const { Title } = Typography;

const axios = axiosBase.create();

const RackRegistryView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectValues, setSelectValues] = useState({});
  const [photos, setPhotos] = useState([]);
  const [diagramData, setDiagramData] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [activeLink, setActiveLink] = useState(null);
  const [anchorVersion, setAnchorVersion] = useState(0);
  const history = useHistory();
  const { id } = useParams();
  const printRef = useRef();
  const diagramContainerRef = useRef();
  const portAnchors = useRef({});

  const diagramViewMode = data?.diagram_view_mode || 'basic';

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rackRegistryGetByIdService(id);
      if (response.data.items && response.data.items.length > 0) {
        setData(response.data.items[0]);
      }
    } catch (error) {
      message.error('Failed to fetch rack registry data');
      history.push('/rackregistry');
    } finally {
      setLoading(false);
    }
  }, [id, history]);

  const fetchDiagramData = useCallback(async () => {
    try {
      const response = await rackRegistryDiagramDataService(id);
      if (response.data.items && response.data.items.length > 0) {
        setDiagramData(response.data.items[0]);
      }
    } catch (error) {
      setDiagramData(null);
    }
  }, [id]);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`/rack-photos/module/${id}`);
      if (response.data && response.data.items) {
        setPhotos(response.data.items || []);
      } else {
        setPhotos([]);
      }
    } catch (error) {
      setPhotos([]);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    fetchAvailableDevices();
    fetchPhotos();
  }, [id, fetchData, fetchPhotos]);

  useEffect(() => {
    if (diagramViewMode === 'enhanced') {
      fetchDiagramData();
    } else {
      setDiagramData(null);
      setSelectedPort(null);
      setActiveLink(null);
    }
  }, [diagramViewMode, fetchDiagramData]);

  const fetchAvailableDevices = async () => {
    try {
      const response = await rackDeviceListService();
      if (response.data.items) {
        setAvailableDevices(response.data.items);
      }
    } catch (error) {
      message.error('Failed to fetch available devices');
    }
  };

  const getDeviceUCount = (deviceId) => {
    const device = availableDevices.find((d) => d._id === deviceId);
    return device?.number_of_u || 1;
  };

  const isDeviceAlreadyUsed = (deviceId) => {
    if (!data?.mapping) return false;
    return data.mapping.some((mapping) => mapping.rackdevice === deviceId);
  };

  const isBlockedU = (uNumber) => {
    if (!data?.mapping) return false;

    for (const mapping of data.mapping) {
      const deviceUCount = getDeviceUCount(mapping.rackdevice);
      const startU = mapping.u_number;
      const endU =
        data.numbering_direction === 'high-to-low'
          ? startU - deviceUCount + 1
          : startU + deviceUCount - 1;

      if (data.numbering_direction === 'high-to-low') {
        if (uNumber <= startU && uNumber >= endU) return mapping.rackdevice;
      } else {
        if (uNumber >= startU && uNumber <= endU) return mapping.rackdevice;
      }
    }
    return false;
  };

  const handleAddDevice = async (uNumber, deviceId) => {
    try {
      const device = availableDevices.find((d) => d._id === deviceId);
      const deviceUCount = device?.number_of_u || 1;

      if (device?.reuse_option === 'No' && isDeviceAlreadyUsed(deviceId)) {
        message.error(
          `Device "${device.equipment_name}" cannot be reused. It has reuse_option set to "No".`,
        );
        setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
        return;
      }

      const isHighToLow = data.numbering_direction === 'high-to-low';
      const startU = uNumber;
      const endU = isHighToLow
        ? startU - deviceUCount + 1
        : startU + deviceUCount - 1;

      if (isHighToLow) {
        if (endU < 1) {
          message.error(
            `Not enough space above U${uNumber} for a ${deviceUCount}U device`,
          );
          setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
          return;
        }
      } else if (endU > data.total_u) {
        message.error(
          `Not enough space below U${uNumber} for a ${deviceUCount}U device`,
        );
        setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
        return;
      }

      for (let i = 0; i < deviceUCount; i++) {
        const checkU = isHighToLow ? startU - i : startU + i;
        if (checkU !== uNumber && isBlockedU(checkU)) {
          message.error(
            `U${checkU} is already occupied. Cannot place ${deviceUCount}U device at U${uNumber}`,
          );
          setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
          return;
        }
      }

      const newMapping = [...(data.mapping || [])];
      const existingIndex = newMapping.findIndex((m) => m.u_number === uNumber);

      if (existingIndex >= 0) {
        newMapping[existingIndex] = {
          ...newMapping[existingIndex],
          rackdevice: deviceId,
        };
      } else {
        newMapping.push({ u_number: uNumber, rackdevice: deviceId });
      }

      await rackRegistryUpdateService(id, { ...data, mapping: newMapping });
      message.success('Device mapped successfully');
      fetchData();
      if (diagramViewMode === 'enhanced') fetchDiagramData();
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to map device');
      }
      setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
    }
  };

  const handleDeleteMapping = async (uNumber) => {
    try {
      const newMapping = (data.mapping || []).filter(
        (m) => m.u_number !== uNumber,
      );
      await rackRegistryUpdateService(id, { ...data, mapping: newMapping });
      setSelectValues((prev) => ({ ...prev, [uNumber]: undefined }));
      message.success('Device mapping removed');
      fetchData();
      if (diagramViewMode === 'enhanced') fetchDiagramData();
    } catch (error) {
      message.error('Failed to remove device mapping');
    }
  };

  const handleViewModeChange = async (e) => {
    const mode = e.target.value;
    try {
      await rackRegistryUpdateService(id, {
        ...data,
        diagram_view_mode: mode,
      });
      setData((prev) => ({ ...prev, diagram_view_mode: mode }));
      message.success(
        mode === 'enhanced' ? 'Enhanced view enabled' : 'Basic view enabled',
      );
    } catch (error) {
      message.error('Failed to update view mode');
    }
  };

  const getAvailableDevicesForDropdown = () =>
    availableDevices.filter((device) => {
      if (device.reuse_option === 'No') {
        return !isDeviceAlreadyUsed(device._id);
      }
      return true;
    });

  const handlePortClick = (port) => {
    if (!port) return;
    setSelectedPort(port);

    const portId = String(port._id);
    const connectedId = port.connected_portregistry
      ? String(
          typeof port.connected_portregistry === 'object'
            ? port.connected_portregistry._id
            : port.connected_portregistry,
        )
      : null;

    if (connectedId) {
      const link = (diagramData?.connections || []).find(
        (c) =>
          String(c.from_port_id) === portId ||
          String(c.to_port_id) === portId,
      );
      setActiveLink(link || null);
    } else {
      setActiveLink(null);
    }
    setAnchorVersion((v) => v + 1);
  };

  const handleRegisterAnchor = useCallback((portId, el) => {
    if (el) {
      portAnchors.current[String(portId)] = el;
    } else {
      delete portAnchors.current[String(portId)];
    }
  }, []);

  const connectedPortIds = useMemo(() => {
    const ids = new Set();
    (diagramData?.connections || []).forEach((c) => {
      ids.add(String(c.from_port_id));
      ids.add(String(c.to_port_id));
    });
    return ids;
  }, [diagramData]);

  const breadcrumbItems = useRackDiagramBreadcrumb(
    data?.rack_name || 'View Rack',
    '',
  );

  if (!data) return null;

  const uNumbers = Array.from({ length: data.total_u }, (_, i) => i + 1).sort(
    (a, b) => (data.numbering_direction === 'high-to-low' ? b - a : a - b),
  );

  const renderEquipmentName = (device) => {
    if (!device) return 'N/A';
    const foundDevice = availableDevices.find((d) => d._id === device);
    return foundDevice?.equipment_name || 'N/A';
  };

  const groupUNumbers = () => {
    const groups = [];
    const processed = new Set();

    uNumbers.forEach((uNumber) => {
      if (processed.has(uNumber)) return;

      const mapping = (data.mapping || []).find((m) => m.u_number === uNumber);
      const device = mapping?.rackdevice;
      const blockingDevice = isBlockedU(uNumber);

      if (device) {
        const deviceUCount = getDeviceUCount(device);
        const startU = uNumber;
        let uList = [];

        if (data.numbering_direction === 'high-to-low') {
          const endU = startU - deviceUCount + 1;
          for (let u = startU; u >= Math.max(endU, 1); u--) {
            if (isBlockedU(u) === device) {
              uList.push(u);
              processed.add(u);
            }
          }
        } else {
          const endU = startU + deviceUCount - 1;
          for (let u = startU; u <= Math.min(endU, data.total_u); u++) {
            if (isBlockedU(u) === device) {
              uList.push(u);
              processed.add(u);
            }
          }
        }

        if (uList.length > 0) {
          groups.push({
            type: 'device',
            deviceId: device,
            uNumbers: uList.sort((a, b) =>
              data.numbering_direction === 'high-to-low' ? b - a : a - b,
            ),
          });
        }
      } else if (blockingDevice) {
        let startU = uNumber;
        for (const m of data.mapping || []) {
          if (m.rackdevice === blockingDevice) {
            startU = m.u_number;
            break;
          }
        }

        const deviceUCount = getDeviceUCount(blockingDevice);
        let uList = [];
        if (data.numbering_direction === 'high-to-low') {
          const endU = startU - deviceUCount + 1;
          for (let u = startU; u >= Math.max(endU, 1); u--) {
            if (isBlockedU(u) === blockingDevice && !processed.has(u)) {
              uList.push(u);
              processed.add(u);
            }
          }
        } else {
          const endU = startU + deviceUCount - 1;
          for (let u = startU; u <= Math.min(endU, data.total_u); u++) {
            if (isBlockedU(u) === blockingDevice && !processed.has(u)) {
              uList.push(u);
              processed.add(u);
            }
          }
        }

        if (uList.length > 0) {
          groups.push({
            type: 'device',
            deviceId: blockingDevice,
            uNumbers: uList.sort((a, b) =>
              data.numbering_direction === 'high-to-low' ? b - a : a - b,
            ),
          });
        }
      } else {
        groups.push({ type: 'empty', uNumbers: [uNumber], deviceId: null });
        processed.add(uNumber);
      }
    });

    return groups;
  };

  const filteredDevices = getAvailableDevicesForDropdown();

  return (
    <NetworkToolWrapper isBranchClientRequired={false}>
      <MyBreadcrumb items={breadcrumbItems} />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/rackregistry')}
        title={data?.rack_name || 'Rack Diagram'}
        subTitle={diagramViewMode === 'enhanced' ? 'Enhanced view' : 'Basic view'}
        className="print rack-registry-no-print-header"
        extra={[
          <Radio.Group
            key="view-mode"
            value={diagramViewMode}
            onChange={handleViewModeChange}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="basic">Basic</Radio.Button>
            <Radio.Button value="enhanced">Enhanced</Radio.Button>
          </Radio.Group>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print
          </Button>,
        ]}
      >
        <Card
          loading={loading}
          className="printSection rack-registry-print-container"
          style={{ width: '100%' }}
          ref={printRef}
        >
          <div
            style={{
              fontSize: '13px',
              lineHeight: '1.6',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
            }}
          >
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <strong>Rack:</strong> {data?.rack_name}
              </Col>
              {data?.lock_code && (
                <Col span={8}>
                  <strong>Lock:</strong> {data.lock_code}
                </Col>
              )}
              <Col span={8}>
                <strong>Size:</strong> {data?.total_u}U
              </Col>
              {data?.labeled_as && (
                <Col span={8}>
                  <strong>Label:</strong> {data.labeled_as}
                </Col>
              )}
              <Col span={8}>
                <strong>Direction:</strong>
                <Tag
                  color={
                    data?.numbering_direction === 'high-to-low'
                      ? 'blue'
                      : 'green'
                  }
                  style={{ marginLeft: '4px' }}
                >
                  {data?.numbering_direction === 'high-to-low'
                    ? 'High→Low'
                    : 'Low→High'}
                </Tag>
              </Col>
              <Col span={8}>
                {photos && photos.length > 0 ? (
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() =>
                      window.open(`/rackregistry/upload/${id}`, '_blank')
                    }
                    style={{ padding: '0', height: 'auto' }}
                  >
                    View Photos
                  </Button>
                ) : (
                  <span style={{ color: '#999' }}>No Photos</span>
                )}
              </Col>
            </Row>
          </div>

          {data?.notes && (
            <div
              style={{
                marginTop: 24,
                marginBottom: 16,
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef',
              }}
            >
              <strong>Notes:</strong>
              <div style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                {data.notes}
              </div>
            </div>
          )}

          {diagramViewMode === 'enhanced' && <EnhancedModeLegend />}

          {diagramViewMode === 'enhanced' && selectedPort && (
            <PortDetailPanel
              selectedPort={selectedPort}
              onClose={() => {
                setSelectedPort(null);
                setActiveLink(null);
                setAnchorVersion((v) => v + 1);
              }}
            />
          )}

          <Title
            level={3}
            className="rack-registry-print-title"
            style={{ marginTop: 24, marginBottom: 16 }}
          >
            {data?.rack_name || 'Device Mapping to Rack'}
          </Title>
          <div
            ref={diagramContainerRef}
            className="rack-registry-diagram"
            style={{
              border: `2px solid ${diagramViewMode === 'enhanced' ? enhancedTheme.rackBorder : '#000'}`,
              borderRadius: '6px',
              padding: '16px',
              backgroundColor:
                diagramViewMode === 'enhanced'
                  ? enhancedTheme.rackBg
                  : '#f0f0f0',
              boxShadow:
                diagramViewMode === 'enhanced'
                  ? 'inset 0 0 0 1px #fff'
                  : '0 2px 8px rgba(0,0,0,0.15)',
              position: 'relative',
            }}
          >
            {groupUNumbers().map((group, index) => (
              <RackDiagramGroup
                key={`group-${index}`}
                group={group}
                mode={diagramViewMode}
                data={data}
                availableDevices={filteredDevices}
                diagramDevices={diagramData?.devices || {}}
                diagramPorts={diagramData?.ports || []}
                selectValues={selectValues}
                renderEquipmentName={renderEquipmentName}
                onSelectDevice={(uNumber, value) => {
                  setSelectValues((prev) => ({ ...prev, [uNumber]: value }));
                  handleAddDevice(uNumber, value);
                }}
                onDeleteMapping={handleDeleteMapping}
                selectedPortId={
                  selectedPort ? String(selectedPort._id) : null
                }
                connectedPortIds={connectedPortIds}
                onPortClick={handlePortClick}
                onRegisterAnchor={handleRegisterAnchor}
              />
            ))}
            {diagramViewMode === 'enhanced' && (
              <PortLinkOverlay
                containerRef={diagramContainerRef}
                activeLink={activeLink}
                portAnchors={portAnchors}
                anchorVersion={anchorVersion}
              />
            )}
          </div>
        </Card>
      </PageHeader>
    </NetworkToolWrapper>
  );
};

export default RackRegistryView;
